import { ElementButton, InputText } from 'macaron-frontend-ui'
import { useErrorOnBlur } from '~/features/reservationInput/hooks/useErrorOnBlur'
import { sva } from '~/styled-system/css'
import { useDebouncedOnChange } from '../../hooks/useDebounce'
import { placeholder } from '../../lib/placeholder'
import { useLoginProps } from '../../store/reserve'
import { useEmail } from '../../store/stateMachine/context'
import { useEmailError, useIsEmailRegistered, useSetEmail } from '../../store/userInput'
import { AlreadyRegisteredMemberAlert } from './AlreadyRegisteredMemberAlert'

const emailStyles = sva({
  slots: ['container', 'heading', 'title', 'required', 'note'],
  base: {
    container: {
      spaceY: '1',
      md: {
        display: 'flex',
        alignItems: 'center',
        spaceY: '0',
        spaceX: '2',
      },
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      spaceX: '2',
      _pc: {
        display: 'block',
        spaceX: '0',
        width: '[188px]',
        flexShrink: '0',
        spaceY: '1',
      },
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 'md',
    },
    required: {
      ml: '1',
      fontSize: 'sm',
      color: 'accent.pink',
    },
    note: {
      fontSize: 'xs',
    },
  },
})

export function Email({ children }: React.PropsWithChildren) {
  const classes = emailStyles()
  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <p className={classes.title}>
          メールアドレス
          <span className={classes.required}>*</span>
        </p>
        <p className={classes.note}>※半角英数字</p>
      </div>
      {children}
    </div>
  )
}

const emailInputStyles = sva({
  slots: ['container', 'note', 'alert'],
  base: {
    container: {
      width: 'full',
      spaceY: '1',
    },
    alert: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      spaceX: '2',
      rounded: 'default',
      bg: 'gray.100',
      p: '2',
    },
  },
})

export function EmailInput() {
  const { onChange, error, email, registered, onOpen, onBlur } = useEmailInput()
  const classes = emailInputStyles()
  return (
    <div className={classes.container}>
      <InputText
        errorMessage={error}
        width={300}
        placeholder={placeholder.email}
        withSpread={true}
        type="email"
        onChange={onChange}
        onBlur={onBlur}
        defaultValue={email}
      />
      {/* 一休のときだけ既存会員かどうかの確認を行う */}
      {import.meta.env.VITE_MODE === 'ikyu' && registered && (
        <div className={classes.alert}>
          <AlreadyRegisteredMemberAlert
            phoneNumberOrEmail={email}
            type="email"
            registered={registered}
          />
          <ElementButton label="ログイン" color="login" size="xSmall" onClick={onOpen(false)} />
        </div>
      )}
    </div>
  )
}

function useEmailInput() {
  const registered = useIsEmailRegistered()
  const email = useEmail()
  const change = useSetEmail()
  const onChange = useDebouncedOnChange(change, email)
  const emailError = useEmailError()
  const { onOpen } = useLoginProps()
  const { error, onBlur } = useErrorOnBlur(emailError)
  return { onChange, error, email, registered, onOpen, onBlur }
}
